// Wave 20.44 — Session Focus screen.
//
// Sits between "user wants to start an aero session" and Preflight.
// The rider picks ONE position to work on. From here the session
// carries a focusClass through to POST /sessions/start and the
// end-of-session summary, so the whole flow is framed around that
// position ("let's refine your drops" / "let's record a new one").
//
// Tiles come from GET /sessions/me/positions/analytics — every class
// the rider has ever held for ≥30 s in a prior session. A final
// "+ Record a new position" tile always appears.
//
// Deep-link: /session/focus?preselect=<class> auto-highlights that
// tile (from a Wake-label tap). Rider can still switch by tapping a
// different tile.

import React, { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  CssBaseline,
  Stack,
  ThemeProvider,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import LockIcon from '@mui/icons-material/Lock';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AppHeader from './components/AppHeader';
import { theme, colors } from './theme';
import { positionColor } from './services/positionColors';
import { labelForPosition } from './vision/positionClassifier';
import { usePositionsAnalytics } from './hooks/usePositionsAnalytics';
import { useSubscription } from './services/useSubscription';

function classLabel(cls: string): string {
  // labelForPosition only knows the canonical enum; be graceful for
  // anything the backend has learned about but we haven't enumerated.
  const known = labelForPosition(cls as any);
  return known === '—' ? cls.replace(/_/g, ' ') : known;
}

export default function SessionFocus() {
  const nav = useNavigate();
  const [search] = useSearchParams();
  const preselect = search.get('preselect');
  const { data, loading, error, errorMessage } = usePositionsAnalytics();
  // Wave 20.44 — read tier cap. When free tier + at cap, the
  // "Record a new position" tile turns into an upgrade CTA that
  // routes to /subscription instead of starting a new-position
  // session.
  const { usage } = useSubscription();
  const maxPositions = usage?.maxPositions ?? null;

  const positions = useMemo(() => {
    if (!data) return [];
    // Best CdA first — matches the AeroPositionsPanel ordering.
    return [...data.positions].sort((a, b) => a.bestCda - b.bestCda);
  }, [data]);

  const atPositionCap =
    maxPositions != null && positions.length >= maxPositions;

  const goToSession = (focus: string) => {
    nav(`/session?focus=${encodeURIComponent(focus)}`);
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ minHeight: '100vh', backgroundColor: colors.bg }}>
        <AppHeader />
        <Box sx={{ maxWidth: 900, mx: 'auto', px: { xs: 2.5, md: 4 }, py: { xs: 4, md: 6 } }}>
          <Typography
            variant="overline"
            sx={{ color: colors.brand, letterSpacing: '0.24em' }}
          >
            NEW AERO SESSION
          </Typography>
          <Typography
            variant="h3"
            sx={{
              fontSize: { xs: '1.75rem', md: '2.25rem' },
              fontWeight: 900,
              letterSpacing: '-0.02em',
              mt: 1,
              mb: 1,
            }}
          >
            What are you working on?
          </Typography>
          <Typography sx={{ color: colors.textMuted, maxWidth: 620, mb: 4 }}>
            Each session focuses on one position. Pick one to refine, or
            record a new position from scratch.
          </Typography>

          {loading && (
            <Stack alignItems="center" sx={{ py: 6 }}>
              <CircularProgress sx={{ color: colors.brand }} />
            </Stack>
          )}

          {!loading && error === 'paywall' && (
            <Box
              sx={{
                p: 3,
                borderRadius: 3,
                border: `1px solid ${colors.border}`,
                backgroundColor: colors.surface,
                textAlign: 'center',
              }}
            >
              <Typography sx={{ color: colors.textMuted, mb: 2 }}>
                {errorMessage}
              </Typography>
              <Button
                variant="outlined"
                onClick={() => nav('/subscription')}
                sx={{ borderColor: colors.brand, color: colors.brand, fontWeight: 700 }}
              >
                Upgrade
              </Button>
            </Box>
          )}

          {!loading && error && error !== 'paywall' && (
            <Typography sx={{ color: colors.warning }}>{errorMessage}</Typography>
          )}

          {!loading && !error && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, minmax(0, 1fr))',
                  md: 'repeat(3, minmax(0, 1fr))',
                },
                gap: 2,
              }}
            >
              {positions.map((p) => {
                const c = positionColor(p.class);
                const isPreselected = preselect === p.class;
                return (
                  <Box
                    key={p.class}
                    onClick={() => goToSession(p.class)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') goToSession(p.class);
                    }}
                    sx={{
                      p: 2.5,
                      borderRadius: 3,
                      cursor: 'pointer',
                      backgroundColor: colors.surface,
                      border: `1px solid ${
                        isPreselected ? colors.brand : colors.border
                      }`,
                      boxShadow: isPreselected
                        ? `0 0 0 2px ${colors.brandShadow}, 0 16px 40px rgba(106, 255, 69, 0.12)`
                        : 'none',
                      transition: 'border-color 140ms, box-shadow 140ms, transform 140ms',
                      '&:hover': {
                        borderColor: colors.brand,
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        color: c,
                        fontSize: '0.7rem',
                        fontWeight: 800,
                        letterSpacing: '0.14em',
                        textTransform: 'uppercase',
                      }}
                    >
                      Refine
                    </Typography>
                    <Typography
                      sx={{
                        color: colors.text,
                        fontWeight: 900,
                        fontSize: '1.35rem',
                        letterSpacing: '-0.01em',
                        mt: 0.25,
                      }}
                    >
                      {p.alias?.trim() || classLabel(p.class)}
                    </Typography>
                    <Typography
                      sx={{
                        color: colors.textMuted,
                        fontSize: '0.75rem',
                        mt: 0.5,
                      }}
                    >
                      Best CdA{' '}
                      <Box
                        component="span"
                        sx={{
                          fontFamily: '"JetBrains Mono", monospace',
                          fontWeight: 700,
                          color: colors.brand,
                        }}
                      >
                        {p.bestCda.toFixed(3)}
                      </Box>{' '}
                      · {p.sessionCount} session{p.sessionCount === 1 ? '' : 's'}
                    </Typography>
                    {isPreselected && (
                      <Button
                        variant="contained"
                        startIcon={<PlayArrowIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          goToSession(p.class);
                        }}
                        sx={{
                          mt: 2,
                          backgroundColor: colors.brand,
                          color: colors.bg,
                          fontWeight: 700,
                          '&:hover': { backgroundColor: colors.brandBright },
                        }}
                      >
                        Refine this position
                      </Button>
                    )}
                  </Box>
                );
              })}

              {/* "Record a new position" tile. Free-tier riders at
                  cap see a locked upgrade CTA in this slot instead
                  of a session-start tile. */}
              <Box
                onClick={() => (atPositionCap ? nav('/subscription') : goToSession('new'))}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter' && e.key !== ' ') return;
                  atPositionCap ? nav('/subscription') : goToSession('new');
                }}
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  cursor: 'pointer',
                  backgroundColor: atPositionCap ? 'rgba(106,255,69,0.04)' : 'transparent',
                  border: `1px dashed ${atPositionCap ? colors.borderFocus : colors.border}`,
                  transition: 'border-color 140ms, transform 140ms',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'flex-start',
                  minHeight: 128,
                  '&:hover': {
                    borderColor: colors.brand,
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    backgroundColor: 'rgba(106, 255, 69, 0.12)',
                    border: `1px solid ${colors.borderFocus}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 1.25,
                  }}
                >
                  {atPositionCap ? (
                    <LockIcon sx={{ color: colors.brand, fontSize: 20 }} />
                  ) : (
                    <AddIcon sx={{ color: colors.brand, fontSize: 22 }} />
                  )}
                </Box>
                <Typography
                  sx={{
                    color: colors.text,
                    fontWeight: 900,
                    fontSize: '1.05rem',
                    letterSpacing: '-0.01em',
                  }}
                >
                  {atPositionCap ? 'Unlock more positions' : 'Record a new position'}
                </Typography>
                <Typography sx={{ color: colors.textMuted, fontSize: '0.75rem', mt: 0.5 }}>
                  {atPositionCap
                    ? 'Free plan measures one position. Upgrade to measure every position you ride.'
                    : "We'll capture it, then work on reducing drag."}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </ThemeProvider>
  );
}
